import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRight, Coins, Database, TrendingUp } from "lucide-react";

const Calculator = () => {
  const navigate = useNavigate();
  const [roiData, setRoiData] = useState({
    adr: 250,
    ota_nights: 400,
    comm_pct: 20,
    shift_pct: 5,
  });

  // ROI Calculations
  const monthlyLeakage = roiData.ota_nights * roiData.adr * (roiData.comm_pct / 100);
  const monthlySavings = monthlyLeakage * (roiData.shift_pct / 100);
  const annualSavings = monthlySavings * 12;
  const ltvBonus = monthlySavings * 1.3;

  const scrollToAudit = () => {
    navigate("/");
    setTimeout(() => {
      const element = document.getElementById("audit-form");
      element?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <section className="pt-10 pb-24 px-8 max-w-8xl mx-auto">
      <div className="max-w-5xl mx-auto text-center mb-20 space-y-6">
        <h2 className="text-5xl md:text-7xl font-headline font-extrabold tracking-tight text-on-surface">
          AI Revenue &amp; Direct-Shift Calculator
        </h2>
        <p className="text-on-surface-variant text-xl lg:text-2xl">
          The math behind why AI visibility is your most profitable direct booking engine.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Input Column */}
        <div className="p-10 lg:p-14 bg-surface-container-lowest rounded-3xl shadow-cloud space-y-12">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-on-surface uppercase tracking-wider">
                Average Daily Rate (ADR)
              </label>
              <span className="text-2xl font-bold text-primary">${roiData.adr}</span>
            </div>
            <input
              type="range"
              min="100"
              max="2000"
              step="50"
              value={roiData.adr}
              onChange={(e) => setRoiData({ ...roiData, adr: parseInt(e.target.value) })}
              className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-on-surface uppercase tracking-wider">
                Monthly Room Nights via OTAs
              </label>
              <span className="text-2xl font-bold text-primary">{roiData.ota_nights}</span>
            </div>
            <input
              type="range"
              min="50"
              max="5000"
              step="50"
              value={roiData.ota_nights}
              onChange={(e) => setRoiData({ ...roiData, ota_nights: parseInt(e.target.value) })}
              className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-on-surface uppercase tracking-wider">
                Avg. OTA Commission %
              </label>
              <span className="text-2xl font-bold text-primary">{roiData.comm_pct}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="30"
              step="1"
              value={roiData.comm_pct}
              onChange={(e) => setRoiData({ ...roiData, comm_pct: parseInt(e.target.value) })}
              className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-on-surface uppercase tracking-wider">
                Target AI-Direct Shift %
              </label>
              <span className="text-2xl font-bold text-tertiary">{roiData.shift_pct}%</span>
            </div>
            <input
              type="range"
              min="1"
              max="25"
              step="1"
              value={roiData.shift_pct}
              onChange={(e) => setRoiData({ ...roiData, shift_pct: parseInt(e.target.value) })}
              className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer accent-tertiary"
            />
            <p className="text-xs text-on-surface-variant italic">
              Percentage of OTA guests you shift to direct via AI-search recommendations.
            </p>
          </div>
        </div>

        {/* Results Column */}
        <div className="space-y-8">
          <div className="p-10 bg-tertiary/5 rounded-3xl relative overflow-hidden group">
            <TrendingUp className="absolute top-8 right-8 w-10 h-10 text-tertiary opacity-20" />
            <h4 className="text-sm font-bold text-tertiary uppercase tracking-widest mb-4">
              Immediate Monthly Savings
            </h4>
            <div className="text-7xl font-headline font-extrabold text-on-surface mb-4">
              ${Math.round(monthlySavings).toLocaleString()}
            </div>
            <p className="text-on-surface-variant text-base">
              Profit previously lost to OTA commissions.
            </p>
          </div>

          <div className="p-10 bg-surface-container-lowest rounded-3xl shadow-cloud relative group">
            <Coins className="absolute top-8 right-8 w-10 h-10 text-primary opacity-20" />
            <h4 className="text-sm font-bold text-primary uppercase tracking-widest mb-4">
              Total Annual Recovery
            </h4>
            <div className="text-7xl font-headline font-extrabold text-on-surface mb-4">
              ${Math.round(annualSavings).toLocaleString()}
            </div>
            <p className="text-on-surface-variant text-base">
              Direct bottom-line impact by being AI-Ready.
            </p>
          </div>

          <div className="p-10 bg-surface-container-lowest rounded-3xl shadow-cloud relative group">
            <Database className="absolute top-8 right-8 w-10 h-10 text-surface-tint opacity-20" />
            <h4 className="text-sm font-bold text-surface-tint uppercase tracking-widest mb-4">
              Total Value of Direct Bookings
            </h4>
            <div className="text-7xl font-headline font-extrabold text-on-surface mb-4">
              ${Math.round(ltvBonus).toLocaleString()}
            </div>
            <p className="text-on-surface-variant text-base">
              Includes the 30% higher value of direct guest data ownership.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-24 p-16 bg-gradient-to-br from-primary to-primary-container rounded-[3rem] text-center space-y-10 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <h3 className="text-4xl md:text-5xl font-headline font-extrabold text-on-primary leading-tight">
            Your ROI is{" "}
            <span className="text-inverse-on-surface">
              ${Math.round(annualSavings).toLocaleString()}
            </span>
            . <br />
            Our tool costs less than 5% of your recovered profit. <br />
            Stop the leakage today.
          </h3>
          <button
            onClick={scrollToAudit}
            className="mt-10 bg-on-primary text-primary hover:bg-inverse-on-surface px-14 py-6 rounded-xl font-bold transition-all inline-flex items-center justify-center gap-3 text-lg"
          >
            Start My AI Readiness Audit Now <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
